import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
  HiOutlineInformationCircle,
  HiOutlineArrowTopRightOnSquare,
} from "react-icons/hi2";
import { admission } from "../config/api";
import { academicYearFullLabel } from "../config/academicYear";
import { FEE_SECTIONS, buildFeeRow } from "../config/tuitionFee";
import "./Tuitionfees.css";

const REGULATION_URL = "https://reg.kpru.ac.th/th/base_rule.asp";

const bahtFormat = new Intl.NumberFormat("th-TH");

/** cache / in-flight ที่ระดับโมดูล — กัน StrictMode ยิงซ้ำ และไม่โหลดคณะเดิมซ้ำ */
const feeCache = new Map();
const feeInflight = new Map();

function baht(amount) {
  return bahtFormat.format(amount);
}

function range({ min, max }) {
  return min === max ? baht(min) : `${baht(min)} - ${baht(max)}`;
}

function cacheKey(type, fac) {
  return `${type}:${fac}`;
}

function mapFacultyRows(section, faculty, list) {
  const formulaKey = faculty.formula || section.formula;
  return list.map((item) => ({
    ...buildFeeRow(item, formulaKey),
    sectionKey: section.key,
    sectionLabel: section.label,
    facId: faculty.fac,
    facName: faculty.name,
  }));
}

/** ดึง 1 คณะ — ใช้ cache / รวม request ที่ค้างอยู่ */
function fetchFacultyFees(section, faculty) {
  const key = cacheKey(section.type, faculty.fac);
  if (feeCache.has(key)) {
    return Promise.resolve(feeCache.get(key));
  }
  if (feeInflight.has(key)) {
    return feeInflight.get(key);
  }

  const request = axios
    .post(`${admission}/student/Tuitionfee`, {
      type: section.type,
      fac: faculty.fac,
    })
    .then((res) => {
      const list = Array.isArray(res.data) ? res.data : [];
      const rows = mapFacultyRows(section, faculty, list);
      feeCache.set(key, rows);
      return rows;
    })
    .finally(() => {
      feeInflight.delete(key);
    });

  feeInflight.set(key, request);
  return request;
}

function allFacultyJobs() {
  const jobs = [];
  FEE_SECTIONS.forEach((section) => {
    section.faculties.forEach((faculty) => {
      jobs.push({ section, faculty });
    });
  });
  return jobs;
}

function Tuitionfees() {
  const [rows, setRows] = useState([]);
  const [failed, setFailed] = useState(0);
  const [loading, setLoading] = useState(true);

  const [sectionKey, setSectionKey] = useState(FEE_SECTIONS[0].key);
  const [facId, setFacId] = useState(FEE_SECTIONS[0].faculties[0].fac);
  const [query, setQuery] = useState("");

  const searchLoadedRef = useRef(false);

  const mergeRows = (incoming) => {
    setRows((prev) => {
      const next = prev.slice();
      incoming.forEach((row) => {
        const exists = next.some(
          (item) =>
            item.sectionKey === row.sectionKey &&
            item.facId === row.facId &&
            item.major === row.major
        );
        if (!exists) next.push(row);
      });
      return next;
    });
  };

  const section =
    FEE_SECTIONS.find((item) => item.key === sectionKey) || FEE_SECTIONS[0];
  const searching = query.trim() !== "";

  /** โหลดเฉพาะคณะที่เลือก — ไม่ยิงทั้ง 20 กลุ่มตอนเปิดหน้า */
  useEffect(() => {
    if (searching) return undefined;

    let alive = true;
    const faculty =
      section.faculties.find((item) => item.fac === facId) ||
      section.faculties[0];
    const key = cacheKey(section.type, faculty.fac);

    if (feeCache.has(key)) {
      mergeRows(feeCache.get(key));
      setLoading(false);
      setFailed(0);
      return undefined;
    }

    setLoading(true);
    fetchFacultyFees(section, faculty)
      .then((list) => {
        if (!alive) return;
        mergeRows(list);
        setFailed(0);
      })
      .catch(() => {
        if (!alive) return;
        setFailed((count) => count + 1);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [sectionKey, facId, searching, section]);

  /** ค้นหาข้ามทุกภาค/คณะ — โหลดที่เหลือครั้งเดียวเมื่อเริ่มพิมพ์ */
  useEffect(() => {
    if (!searching || searchLoadedRef.current) return undefined;

    const jobs = allFacultyJobs();
    const pending = jobs.filter(
      ({ section: sec, faculty }) =>
        !feeCache.has(cacheKey(sec.type, faculty.fac))
    );

    if (pending.length === 0) {
      const merged = jobs.flatMap(({ section: sec, faculty }) =>
        feeCache.get(cacheKey(sec.type, faculty.fac))
      );
      setRows(merged);
      setFailed(0);
      searchLoadedRef.current = true;
      setLoading(false);
      return undefined;
    }

    let alive = true;
    setLoading(true);

    Promise.allSettled(
      jobs.map(({ section: sec, faculty }) => fetchFacultyFees(sec, faculty))
    )
      .then((results) => {
        if (!alive) return;
        const merged = [];
        let rejected = 0;
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            merged.push(...result.value);
          } else {
            rejected += 1;
          }
        });
        setRows(merged);
        setFailed(rejected);
        searchLoadedRef.current = true;
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [searching]);

  const selectSection = (key) => {
    const next = FEE_SECTIONS.find((item) => item.key === key);
    setSectionKey(key);
    setFacId(next.faculties[0].fac);
  };

  const visibleRows = useMemo(() => {
    const keyword = query.trim();
    if (keyword !== "") {
      return rows.filter((row) => row.major.includes(keyword));
    }
    return rows.filter(
      (row) => row.sectionKey === sectionKey && row.facId === facId
    );
  }, [rows, query, sectionKey, facId]);

  const rangeNotes = useMemo(
    () =>
      visibleRows
        .map((row) => row.rangeNote)
        .filter((note, index, list) => note && list.indexOf(note) === index),
    [visibleRows]
  );

  return (
    <div className="fee-page">
      <div className="fee-inner">
        <header className="fee-head">
          <p className="fee-head__eyebrow">{academicYearFullLabel()}</p>
          <h1 className="fee-head__title">อัตราค่าธรรมเนียมการศึกษา</h1>
          <p className="fee-head__desc">
            เลือกภาคเรียนและคณะของตัวเอง หรือพิมพ์ชื่อสาขาเพื่อค้นหาได้ทันที
          </p>
          <a
            className="fee-head__link"
            href={REGULATION_URL}
            target="_blank"
            rel="noreferrer"
          >
            ดูระเบียบ ข้อบังคับฉบับเต็ม
            <HiOutlineArrowTopRightOnSquare />
          </a>
        </header>

        <section className="fee-explain">
          <h2>
            <HiOutlineInformationCircle />
            อ่านตารางนี้อย่างไร
          </h2>
          <dl>
            <div>
              <dt>ค่าเทอม</dt>
              <dd>
                ค่าธรรมเนียมการศึกษาแบบเหมาจ่าย เก็บทุกภาคเรียนที่ลงทะเบียน
              </dd>
            </div>
            <div>
              <dt>ค่าธรรมเนียมแรกเข้า</dt>
              <dd>
                จ่าย <strong>ครั้งเดียว</strong> ตอนเข้าเป็นนักศึกษาใหม่
                ไม่ได้เก็บทุกภาคเรียน
              </dd>
            </div>
            <div>
              <dt>รวมจ่ายภาคเรียนแรก</dt>
              <dd>
                ยอดที่ต้องเตรียมจริงตอนรายงานตัว = ค่าเทอม + ค่าธรรมเนียมแรกเข้า
              </dd>
            </div>
            <div>
              <dt>ตลอดหลักสูตร</dt>
              <dd>
                ประมาณการรวมทุกภาคเรียน อาจเปลี่ยนตามแผนการเรียนจริงของแต่ละคน
              </dd>
            </div>
          </dl>
        </section>

        <div className="fee-search">
          <HiOutlineMagnifyingGlass />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ค้นหาชื่อสาขาวิชา เช่น คณิตศาสตร์ การบัญชี"
            aria-label="ค้นหาชื่อสาขาวิชา"
          />
          {searching ? (
            <button type="button" onClick={() => setQuery("")}>
              <HiOutlineXMark />
              ล้างคำค้นหา
            </button>
          ) : null}
        </div>

        {searching ? (
          <p className="fee-result-label">
            ผลการค้นหา “{query.trim()}” พบ {visibleRows.length} สาขา
            จากทุกภาคเรียนและทุกคณะ
          </p>
        ) : (
          <>
            <div className="fee-filters">
              <p className="fee-filters__label">ภาคเรียน / ศูนย์การศึกษา</p>
              <div className="fee-filters__row">
                {FEE_SECTIONS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`fee-pill${
                      item.key === sectionKey ? " is-active" : ""
                    }`}
                    onClick={() => selectSection(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="fee-filters">
              <p className="fee-filters__label">คณะ</p>
              <div className="fee-filters__row">
                {section.faculties.map((faculty) => (
                  <button
                    key={faculty.fac}
                    type="button"
                    className={`fee-pill${
                      faculty.fac === facId ? " is-active" : ""
                    }`}
                    onClick={() => setFacId(faculty.fac)}
                  >
                    {faculty.name}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {failed > 0 ? (
          <p className="fee-warning">
            โหลดข้อมูลไม่สำเร็จ {failed} กลุ่ม บางคณะอาจยังไม่มีข้อมูลแสดง
            ลองรีเฟรชหน้าอีกครั้ง
          </p>
        ) : null}

        {loading ? (
          <p className="fee-status">กำลังโหลดอัตราค่าธรรมเนียม...</p>
        ) : visibleRows.length === 0 ? (
          <p className="fee-status fee-status--empty">
            {searching
              ? "ไม่พบสาขาที่ตรงกับคำค้นหา ลองพิมพ์คำสั้นลง เช่น ชื่อวิชาเอกคำเดียว"
              : "ยังไม่มีข้อมูลค่าธรรมเนียมของคณะนี้"}
          </p>
        ) : (
          <>
            <div className="fee-table-wrap">
              <table className="fee-table">
                <thead>
                  <tr>
                    <th scope="col">สาขาวิชา / วิชาเอก</th>
                    <th scope="col">ค่าเทอม (ต่อภาคเรียน)</th>
                    <th scope="col">ค่าธรรมเนียมแรกเข้า (ครั้งเดียว)</th>
                    <th scope="col">รวมจ่ายภาคเรียนแรก</th>
                    <th scope="col">ประมาณการตลอดหลักสูตร</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={`${row.sectionKey}-${row.facId}-${row.major}`}>
                      <th scope="row" data-label="สาขาวิชา / วิชาเอก">
                        {row.major}
                        {searching ? (
                          <small>
                            {row.sectionLabel} · {row.facName}
                          </small>
                        ) : null}
                      </th>
                      <td data-label="ค่าเทอม (ต่อภาคเรียน)">
                        {range(row.termFee)} บาท
                      </td>
                      <td data-label="ค่าธรรมเนียมแรกเข้า (ครั้งเดียว)">
                        {baht(row.entryFee)} บาท
                        {row.entryParts.length > 0 ? (
                          <small>
                            {row.entryParts
                              .map((part) => `${part.label} ${baht(part.amount)}`)
                              .join(" · ")}
                          </small>
                        ) : null}
                      </td>
                      <td
                        className="fee-table__highlight"
                        data-label="รวมจ่ายภาคเรียนแรก"
                      >
                        {range(row.firstTerm)} บาท
                      </td>
                      <td data-label="ประมาณการตลอดหลักสูตร">
                        {range(row.allCourse)} บาท
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {rangeNotes.length > 0 ? (
              <ul className="fee-footnote">
                {rangeNotes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default Tuitionfees;
